/**
 * FlashlightCard — single flashlight card.
 *
 * Adds a fourth state on top of the standard pattern: 'permanent' —
 * shown with a blue border and the assigned name in blue. Permanent
 * flashlights have no clickable rows.
 */
import { Card, StatusDot, dotLabelColor } from '../ui'
import type { Flashlight } from '../../stores/desktop'
import FlashSVG from './FlashSVG'

interface FlashlightCardProps {
  f: Flashlight
  onUpdate: (patch: Partial<Flashlight>) => void
}

export default function FlashlightCard({ f, onUpdate }: FlashlightCardProps) {
  const isPerm     = !!f.permanent
  const isOos      = !isPerm && !!(f as any).oos
  const isOut      = !isOos && !f.available && !!f.assignedTo
  const isAvail    = !isOos && f.available && !isPerm
  const isNotAvail = !isOos && !f.available && !f.assignedTo && !isPerm

  const borderColor = isOos      ? '#fca5a5'
                    : isPerm     ? '#60a5fa'
                    : isOut      ? '#fb923c'
                    : isNotAvail ? '#f87171'
                    : isAvail    ? '#4ade80'
                                 : '#e5e7eb'

  return (
    <Card variant="item" borderColor={borderColor}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--text-muted)',
          marginBottom: 6,
          textAlign: 'center',
        }}
      >
        Flashlight {f.id}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flexShrink: 0, width: 52, height: 52 }}>
          <FlashSVG />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
          {isPerm ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <StatusDot variant="blue" active={true} />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8' }}>{f.assignedTo}</span>
              </div>
              <span style={{ fontSize: 10, color: '#60a5fa', fontStyle: 'italic' }}>Permanent</span>
            </>
          ) : isOos ? (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>OOS</span>
          ) : (
            <>
              {/* Available */}
              <div
                onClick={() => onUpdate({ available: true, assignedTo: '' })}
                style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', opacity: isAvail ? 1 : 0.3 }}
              >
                <StatusDot variant="green" active={isAvail} />
                <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('green', isAvail) }}>
                  Available
                </span>
              </div>
              {/* Out (display only) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: isOut ? 1 : 0.3 }}>
                <StatusDot variant="orange" active={isOut} />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: dotLabelColor('orange', isOut),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 85,
                  }}
                >
                  {isOut ? f.assignedTo : 'Unassigned'}
                </span>
              </div>
              {/* Not Available */}
              <div
                onClick={() => onUpdate({ available: false })}
                style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', opacity: isNotAvail ? 1 : 0.3 }}
              >
                <StatusDot variant="red" active={isNotAvail} />
                <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('red', isNotAvail) }}>
                  Not Avail.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}
